import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";

import { useChatUnreadContext } from "context/ChatUnreadContext";

import { ChatInboxEmptyState } from "./ChatInboxEmptyState";
import { ChatInboxFilterTabs } from "./ChatInboxFilterTabs";
import { ChatInboxHeader } from "./ChatInboxHeader";
import { ChatInboxRow } from "./ChatInboxRow";
import { ChatInboxSearch } from "./ChatInboxSearch";
import { filterInboxRowsByQuery } from "./utils";

import type { UIEvent } from "react";

import type { InboxChat } from "context/ChatUnreadContext";

import type { ChatInboxFilter } from "./ChatInboxFilterTabs";

const INBOX_PAGE_SIZE = 8;
const SCROLL_LOAD_MORE_THRESHOLD_PX = 60;

interface ChatInboxPanelProps {
  isOpen: boolean;
  onRowClick: (row: InboxChat) => void;
}

interface EmptyStateCopy {
  title: string;
  subtitle: string;
}

function getEmptyStateCopy(
  filter: ChatInboxFilter,
  query: string,
): EmptyStateCopy {
  if (query.length > 0) {
    return {
      title: "No messages found",
      subtitle: `No results for "${query}"`,
    };
  }
  if (filter === "unread") {
    return {
      title: "No unread messages",
      subtitle: "You're all caught up!",
    };
  }
  return {
    title: "No messages",
    subtitle: "Your conversations will appear here.",
  };
}

export function ChatInboxPanel(props: ChatInboxPanelProps) {
  const { isOpen, onRowClick } = props;
  const { inboxArray, unreadCount, truncated } = useChatUnreadContext();

  const [filter, setFilter] = useState<ChatInboxFilter>("all");
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState<number>(INBOX_PAGE_SIZE);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFilter("all");
    setQuery("");
    setVisibleCount(INBOX_PAGE_SIZE);
  }, [isOpen]);

  const filteredRows = useMemo(() => {
    const tabFiltered =
      filter === "unread"
        ? inboxArray.filter((row) => row.unread_count > 0)
        : inboxArray;
    return filterInboxRowsByQuery(tabFiltered, query);
  }, [inboxArray, filter, query]);

  const visibleRows = filteredRows.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRows.length;

  const resetVisibility = () => {
    setVisibleCount(INBOX_PAGE_SIZE);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const handleFilterChange = (next: ChatInboxFilter) => {
    setFilter(next);
    setQuery("");
    resetVisibility();
  };

  const handleSearchChange = (next: string) => {
    setQuery(next);
    resetVisibility();
  };

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!hasMore) return;
      const target = event.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (
        scrollHeight - scrollTop - clientHeight <
        SCROLL_LOAD_MORE_THRESHOLD_PX
      ) {
        setVisibleCount((prev) =>
          Math.min(prev + INBOX_PAGE_SIZE, filteredRows.length),
        );
      }
    },
    [hasMore, filteredRows.length],
  );

  const emptyState = getEmptyStateCopy(filter, query);

  return (
    <Stack sx={{ width: "100%", height: "100%" }}>
      <ChatInboxHeader unreadCount={unreadCount} />
      <ChatInboxFilterTabs
        value={filter}
        unreadCount={unreadCount}
        onChange={handleFilterChange}
      />
      <ChatInboxSearch value={query} onChange={handleSearchChange} />
      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: "scroll" }}
      >
        {visibleRows.length === 0 ? (
          <ChatInboxEmptyState
            title={emptyState.title}
            subtitle={emptyState.subtitle}
          />
        ) : (
          visibleRows.map((row) => (
            <ChatInboxRow
              key={row.channel_name}
              row={row}
              onClick={onRowClick}
            />
          ))
        )}
      </Box>
      {truncated && (
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1.25,
            textAlign: "center",
            color: "text.secondary",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          Showing your 100 most recent chats. Use the Shipments page filters to
          find older shipments and open chat from there.
        </Typography>
      )}
    </Stack>
  );
}
